import { PlusCircle } from "react-feather";
import { Link } from "react-router-dom";
import { Select, Modal, Form, Input, Button, Table, Popconfirm, Upload, message, Tabs, DatePicker } from "antd";
import React, { useState, useEffect } from "react";
import { ArrowLeft, Calendar } from "lucide-react";
import moment from "moment";
import {
  submitApplication,
  getAllApplications,
  getApplicationById,
  updateApplicationStatus,
  deleteApplication,
  getAllJobPostings,
  searchApplications,
  scheduleInterview,
  getAllInterviews,
  getInterviewById,
  updateInterview,
  deleteInterview,
  generateOfferLetter,
  downloadOfferLetter,
  sendOfferLetterEmail
} from "../../service/recruitmentService";
import { getAllEmployeesMethod } from "../../service/ApiServices";

const Interviews = () => {
  const [form] = Form.useForm();
  const [scheduleForm] = Form.useForm();
  const [updateForm] = Form.useForm();
  const [offerForm] = Form.useForm();
  const [openApplyModal, setOpenApplyModal] = useState(false);
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [openScheduleModal, setOpenScheduleModal] = useState(false);
  const [openInterviewDetailsModal, setOpenInterviewDetailsModal] = useState(false);
  const [openUpdateInterviewModal, setOpenUpdateInterviewModal] = useState(false);
  const [openOfferModal, setOpenOfferModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [applications, setApplications] = useState([]);
  const [interviews, setInterviews] = useState([]);
  const [jobPostings, setJobPostings] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 5, total: 0 });
  const [interviewPagination, setInterviewPagination] = useState({ current: 1, pageSize: 5, total: 0 });
  const [pageSizeModalVisible, setPageSizeModalVisible] = useState(false);
  const [tempPageSize, setTempPageSize] = useState(pagination.pageSize);
  const [jobTitle, setJobTitle] = useState("");
  const [candidateName, setCandidateName] = useState("");
  const [status, setStatus] = useState("");
  const pageSizeOptions = [5, 10, 20, 50, 100];
  const branchOptions = [
    { value: "wattala", label: "Wattala" },
    { value: "negombo", label: "Negombo" },
    { value: "kaduwela", label: "Kaduwela" },
    { value: "kiribathgoda", label: "Kiribathgoda" },
  ];

  // Define fetchInterviews at the component level
  const fetchInterviews = async () => {
    setLoading(true);
    try {
      const data = await getAllInterviews();
      console.log('Fetched interviews:', data);
      if (Array.isArray(data)) {
        setInterviews(data);
        setInterviewPagination((prev) => ({ ...prev, total: data.length }));
      } else {
        setInterviews([]);
        setInterviewPagination((prev) => ({ ...prev, total: 0 }));
        message.error("Failed to fetch interviews");
      }
    } catch (error) {
      console.error('Error fetching interviews:', error);
      message.error("Error fetching interviews: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchApplications = async () => {
      setLoading(true);
      const data = await getAllApplications();
      setLoading(false);
      if (Array.isArray(data)) {
        setApplications(data);
        setPagination((prev) => ({ ...prev, total: data.length }));
      } else {
        setApplications([]);
        setPagination((prev) => ({ ...prev, total: 0 }));
      }
    };
    fetchApplications();
  }, []);

  useEffect(() => {
    const fetchJobPostings = async () => {
      const data = await getAllJobPostings();
      if (Array.isArray(data)) {
        setJobPostings(data.filter((job) => job.status === "Open"));
      } else {
        setJobPostings([]);
      }
    };
    fetchJobPostings();
  }, []);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        await getAllEmployeesMethod({
          setEmployeeData: (responseData) => {
            const employeeArray = responseData.data || [];
            setEmployees(employeeArray);
          },
          setLoading: setLoading,
          params: { page: 1, limit: 1000 },
          search: "",
        });
      } catch (error) {
        console.error("Error fetching employees:", error);
        setEmployees([]);
        message.error("Failed to fetch employees");
      }
    };
    fetchEmployees();
  }, []);

  useEffect(() => {
    fetchInterviews(); // Call the defined function
  }, []);

  const fetchApplications = async () => {
    setLoading(true);
    const data = await getAllApplications();
    setLoading(false);
    if (Array.isArray(data)) {
      setApplications(data);
      setPagination((prev) => ({ ...prev, total: data.length }));
    } else {
      setApplications([]);
      setPagination((prev) => ({ ...prev, total: 0 }));
    }
  };

  const handleSearchApplications = async () => {
    setLoading(true);
    try {
      const data = await searchApplications({ jobTitle, candidateName, status });
      if (Array.isArray(data)) {
        setApplications(data);
        setPagination((prev) => ({ ...prev, total: data.length, current: 1 }));
      } else {
        setApplications([]);
        setPagination((prev) => ({ ...prev, total: 0, current: 1 }));
      }
    } catch (error) {
      message.error("Error searching applications: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  const handleInterviewTableChange = (pagination) => {
    setInterviewPagination(pagination);
  };

  const handlePageSizeChange = () => {
    const newPagination = { ...pagination, pageSize: tempPageSize, current: 1 };
    setPagination(newPagination);
    setPageSizeModalVisible(false);
  };

  const onApplyFinish = async (values) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("job_id", values.job_id);
      formData.append("candidate_name", values.candidate_name);
      formData.append("email", values.email);
      if (values.resume && values.resume.fileList && values.resume.fileList[0]) {
        formData.append("resume", values.resume.fileList[0].originFileObj);
      } else {
        message.error("Please upload a resume");
        setLoading(false);
        return;
      }

      const result = await submitApplication(formData);
      if (result) {
        message.success("Application submitted successfully");
        await fetchApplications();
        setOpenApplyModal(false);
        form.resetFields();
      } else {
        message.error("Failed to submit application");
      }
    } catch (error) {
      message.error("Error submitting application: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, status) => {
    setLoading(true);
    try {
      await updateApplicationStatus(id, status);
      await fetchApplications();
      message.success("Status updated successfully");
    } catch (error) {
      message.error("Error updating status: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (id) => {
    setLoading(true);
    try {
      const data = await getApplicationById(id);
      if (data) {
        setSelectedApplication(data);
        setOpenDetailsModal(true);
      } else {
        message.error("Failed to fetch application details");
      }
    } catch (error) {
      message.error("Error fetching details: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadResume = (resume) => {
    try {
      if (!resume.data || typeof resume.data !== 'string') {
        throw new Error('Base64 data is missing or invalid');
      }
      if (!/^[A-Za-z0-9+/=]+$/.test(resume.data)) {
        throw new Error('Base64 string contains invalid characters');
      }
      console.log('Base64 length:', resume.data.length, 'First 50 chars:', resume.data.substring(0, 50));

      const byteCharacters = atob(resume.data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: resume.contentType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = resume.originalName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error, 'Base64 sample:', resume.data?.substring(0, 50));
      message.error("Failed to download resume: " + error.message);
    }
  };

  const handleScheduleInterview = (application) => {
    setSelectedApplication(application);
    setOpenScheduleModal(true);
  };

  const onScheduleInterviewFinish = async (values) => {
    setLoading(true);
    try {
      const interviewData = {
        application_id: selectedApplication._id,
        interviewers: values.interviewers,
        date: values.date.toISOString(),
        mode: values.mode,
        details: values.details,
      };
      const result = await scheduleInterview(interviewData);
      if (result) {
        message.success("Interview scheduled successfully");
        await fetchInterviews();
        setOpenScheduleModal(false);
        scheduleForm.resetFields();
      } else {
        message.error("Failed to schedule interview");
      }
    } catch (error) {
      message.error("Error scheduling interview: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleViewInterviewDetails = async (id) => {
    setLoading(true);
    try {
      const data = await getInterviewById(id);
      if (data) {
        setSelectedInterview(data);
        setOpenInterviewDetailsModal(true);
      } else {
        message.error("Failed to fetch interview details");
      }
    } catch (error) {
      message.error("Error fetching interview details: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateInterview = (interview) => {
    setSelectedInterview(interview);
    updateForm.setFieldsValue({
      feedback: interview.feedback || "",
      status: interview.status,
    });
    setOpenUpdateInterviewModal(true);
  };

  const onUpdateInterviewFinish = async (values) => {
    setLoading(true);
    try {
      const interviewData = {
        feedback: values.feedback,
        status: values.status,
      };
      const result = await updateInterview(selectedInterview._id, interviewData);
      if (result) {
        message.success("Interview updated successfully");
        await fetchInterviews();
        setOpenUpdateInterviewModal(false);
        updateForm.resetFields();
      } else {
        message.error("Failed to update interview");
      }
    } catch (error) {
      message.error("Error updating interview: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateOfferLetter = (interview) => {
    if (interview.status !== 'Completed') {
      message.error("Offer letter can only be generated for completed interviews");
      return;
    }
    console.log('Selected interview for offer letter:', interview);
    setSelectedInterview(interview);
    setOpenOfferModal(true);
  };

  const onGenerateOfferLetterFinish = async (values) => {
    setLoading(true);
    try {
      const offerData = {
        application_id: selectedInterview.application_id._id,
        interview_id: selectedInterview._id,
        offer_details: values.offer_details,
        branch: values.branch,
        onBoard_date: values.onBoard_date.toISOString(),
      };
      console.log('Generating offer letter with data:', offerData);
      const result = await generateOfferLetter(offerData);
      if (result && result._id) {
        message.success("Offer letter generated successfully");
        await handleDownloadOfferLetter(result._id);
        await fetchInterviews();
        setOpenOfferModal(false);
        offerForm.resetFields();
      } else {
        message.error("Failed to generate offer letter: No ID returned");
      }
    } catch (error) {
      console.error('Error generating offer letter:', error.response?.data || error);
      message.error("Error generating offer letter: " + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSendOfferLetterEmail = async (offerLetterId) => {
    if (!offerLetterId) {
      console.error('No offer letter ID provided for send email');
      message.error("No offer letter ID provided");
      return;
    }
    setLoading(true);
    try {
      console.log('Sending email for offer letter ID:', offerLetterId);
      const result = await sendOfferLetterEmail(offerLetterId);
      if (result) {
        message.success("Offer letter email sent successfully");
        await fetchInterviews();
      } else {
        message.error("Failed to send offer letter email");
      }
    } catch (error) {
      console.error('Error sending offer letter email:', error.response?.data || error);
      message.error("Error sending offer letter email: " + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadOfferLetter = async (offerLetterId) => {
    if (!offerLetterId) {
      console.error('No offer letter ID provided for download');
      message.error("No offer letter ID provided");
      return;
    }
    setLoading(true);
    try {
      console.log('Downloading offer letter ID:', offerLetterId);
      const blob = await downloadOfferLetter(offerLetterId);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `offer_letter_${selectedInterview?.application_id?.candidate_name || 'candidate'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      message.success("Offer letter downloaded successfully");
    } catch (error) {
      console.error('Error downloading offer letter:', error.response?.data || error);
      message.error("Failed to download offer letter: " + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const applicationColumns = [
    {
      title: "Job Title",
      dataIndex: "job_id",
      key: "job_id",
      render: (job_id) => job_id?.title || "N/A",
    },
    {
      title: "Candidate Name",
      dataIndex: "candidate_name",
      key: "candidate_name",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
  {
  title: "Status",
  dataIndex: "status",
  key: "status",
  render: (status, record) => (
    <Select
      value={status}
      onChange={(value) => handleStatusChange(record._id, value)}
      style={{
        width: 120,
        color:
          status === "Pending" ? "#FFA500" : // Orange for Pending
          status === "Reviewed" ? "#1E90FF" : // Blue for Reviewed
          status === "Accepted" ? "#008000" : // Green for Accepted
          status === "Rejected" ? "#FF0000" : // Red for Rejected
          "inherit",
      }}
      dropdownStyle={{ minWidth: 120 }}
    >
      {["Pending", "Reviewed", "Rejected", "Accepted"].map((s) => (
        <Select.Option
          key={s}
          value={s}
          style={{
            color:
              s === "Pending" ? "#FFA500" :
              s === "Reviewed" ? "#1E90FF" :
              s === "Accepted" ? "#008000" :
              s === "Rejected" ? "#FF0000" :
              "inherit",
          }}
        >
          {s}
        </Select.Option>
      ))}
    </Select>
  ),
},
    {
      title: "Applied Date",
      dataIndex: "applied_at",
      key: "applied_at",
      render: (applied_at) => (applied_at ? moment(applied_at).format("YYYY-MM-DD") : "N/A"),
    },
    {
      title: "Actions",
      key: "action",
      render: (record) => (
        <td className="action-table-data">
          <Link
            className="me-2 p-2"
            to="#"
            onClick={() => handleViewDetails(record._id)}
            title="View application details"
          >
            <i data-feather="eye" className="feather-eye"></i>
          </Link>
          <Link
            className="me-2 p-2"
            to="#"
            onClick={() => handleScheduleInterview(record)}
            title="Schedule interview"
          >
            <Calendar className="feather-calendar" />
          </Link>
          <Popconfirm
            title="Delete the Application"
            description="Are you sure to delete this application?"
            onConfirm={async () => {
              try {
                await deleteApplication(record._id);
                await fetchApplications();
                message.success("Application deleted successfully");
              } catch (error) {
                message.error("Error deleting application: " + error.message);
              }
            }}
            onCancel={() => console.log("Cancelled")}
            okText="Yes"
            cancelText="No"
          >
            <Link className="confirm-text p-2" title="Delete application" style={{ display: "none" }}>
              <i data-feather="trash-2" className="feather-trash-2"></i>
            </Link>
          </Popconfirm>
        </td>
      ),
    },
  ];

  const interviewColumns = [
    {
      title: "Job Title",
      dataIndex: "application_id",
      key: "job_title",
      render: (application_id) => application_id?.job_id?.title || "N/A",
    },
    {
      title: "Candidate Name",
      dataIndex: "application_id",
      key: "candidate_name",
      render: (application_id) => application_id?.candidate_name || "N/A",
    },
    {
      title: "Interviewers",
      dataIndex: "interviewers",
      key: "interviewers",
      render: (interviewers) => interviewers?.map(emp => emp.full_name).join(', ') || "N/A",
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (date) => (date ? moment(date).format("YYYY-MM-DD HH:mm") : "N/A"),
    },
    {
      title: "Mode",
      dataIndex: "mode",
      key: "mode",
      render: (mode) => mode || "N/A",
    },
    {
      title: "Details",
      dataIndex: "details",
      key: "details",
      render: (details) => details || "N/A",
    },
 {
  title: "Status",
  dataIndex: "status",
  key: "status",
  render: (status) => (
    <span
      style={{
        color:
          status === "Scheduled" ? "#FFA500" : // Orange for Scheduled
          status === "Completed" ? "#008000" : // Green for Completed
          status === "Cancelled" ? "#FF0000" : // Red for Cancelled
          "inherit",
      }}
    >
      {status || "N/A"}
    </span>
  ),
},
    {
      title: "Feedback",
      dataIndex: "feedback",
      key: "feedback",
      render: (feedback) => feedback || "N/A",
    },
    {
      title: "Action",
      key: "action",
      render: (record) => (
        <td className="action-table-data">
          <Link
            className="me-2 p-2"
            to="#"
            onClick={() => handleViewInterviewDetails(record._id)}
            title="View interview details"
          >
            <i data-feather="eye" className="feather-eye"></i>
          </Link>
          <Link
            className="me-2 p-2"
            to="#"
            onClick={() => handleUpdateInterview(record)}
            title="Update interview"
          >
            <i data-feather="edit" className="feather-edit"></i>
          </Link>
          <Popconfirm
            title="Delete the Interview"
            description="Are you sure to delete this interview?"
            onConfirm={async () => {
              try {
                console.log('Deleting interview with ID:', record._id);
                await deleteInterview(record._id);
                await fetchInterviews();
                message.success("Interview deleted successfully");
              } catch (error) {
                console.error('Delete interview error:', error.response?.data);
                message.error(`Error deleting interview: ${error.response?.data?.message || error.message}`);
              }
            }}
            onCancel={() => console.log("Cancelled")}
            okText="Yes"
            cancelText="No"
          >
            <Link className="confirm-text p-2" title="Delete interview">
              <i data-feather="trash-2" className="feather-trash-2"></i>
            </Link>
          </Popconfirm>
        </td>
      ),
    },
  ];

  const onBoardColumns = [
    {
      title: "Job Title",
      dataIndex: "application_id",
      key: "job_title",
      render: (application_id) => application_id?.job_id?.title || "N/A",
    },
    {
      title: "Candidate Name",
      dataIndex: "application_id",
      key: "candidate_name",
      render: (application_id) => application_id?.candidate_name || "N/A",
    },
    {
      title: "Interview Status",
      dataIndex: "status",
      key: "status",
    },
    {
      title: "Feedback",
      dataIndex: "feedback",
      key: "feedback",
      render: (feedback) => feedback || "N/A",
    },
    {
      title: "Offer Letter",
      key: "offer_letter",
      render: (record) => {
        console.log('Rendering offer letter for interview:', record._id, 'Offer letter:', record.offerLetter);
        const offerLetter = record.offerLetter;
        return (
          <td className="action-table-data">
            {!offerLetter || !offerLetter._id ? (
              <Link
                className="me-2 p-2"
                to="#"
                onClick={() => handleGenerateOfferLetter(record)}
                title="Generate offer letter"
              >
                <i data-feather="file-plus" className="feather-file-plus"></i>
              </Link>
            ) : (
              <>
                <Link
                  className="me-2 p-2"
                  to="#"
                  onClick={() => handleDownloadOfferLetter(offerLetter._id)}
                  title="Download offer letter"
                >
                  <i data-feather="download" className="feather-download"></i>
                </Link>
                <Link
                  className="me-2 p-2"
                  to="#"
                  onClick={() => handleSendOfferLetterEmail(offerLetter._id)}
                  title="Send offer letter email"
                >
                  <i data-feather="mail" className="feather-mail"></i>
                </Link>
              </>
            )}
          </td>
        );
      },
    },
  ];

  const renderJobOption = (job) => (
    <div>
      <div><strong>{job.title}</strong></div>
      <div>Requirements: {job.requirements?.length ? job.requirements.join(", ") : "N/A"}</div>
      <div>Due Date: {job.dueDate ? moment(job.dueDate).format("YYYY-MM-DD") : "N/A"}</div>
    </div>
  );

  return (
    <div className="page-wrapper-lt">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="header-leave-container">
              <div>
                <ArrowLeft size={32} />
              </div>
              <div className="page-title">
                <h4>Interviews</h4>
                <h6>Manage applications and schedule interviews</h6>
              </div>
            </div>
            <div className="page-btn" style={{ display: "none" }}>
              <a
                className="btn btn-added"
                onClick={() => setOpenApplyModal(true)}
                title="Apply for a job"
              >
                <PlusCircle className="me-2" />
                Apply
              </a>
            </div>
          </div>
        </div>

        <div className="card table-list-card-grid">
          <div className="card-body" style={{ maxWidth: "100%", marginRight: "20px" }}>
            <div
              className="container mt-3"
              style={{ maxWidth: "100%", marginLeft: "20px", marginRight: "40px" }}
            >
              <div className="row d-flex flex-wrap align-items-start justify-content-between">
                <div className="col-12 col-md-3 text-start">
                  <label className="form-label">Job Title</label>
                  <Input
                    value={jobTitle}
                    onChange={(e) => setJobTitle(e.target.value)}
                    placeholder="Enter job title"
                    style={{ width: "80%", height: "40px" }}
                  />
                </div>
                <div className="col-12 col-md-3 mb-3 text-start">
                  <label className="form-label">Candidate Name</label>
                  <Input
                    value={candidateName}
                    onChange={(e) => setCandidateName(e.target.value)}
                    placeholder="Enter candidate name"
                    style={{ width: "80%", height: "40px" }}
                  />
                </div>
                <div className="col-12 col-md-3 mb-3 text-start">
                  <label className="form-label">Status</label>
                  <Select
                    value={status}
                    onChange={(value) => setStatus(value)}
                    placeholder="Select status"
                    allowClear
                    style={{ width: "80%", height: "40px" }}
                  >
                    {["Pending", "Reviewed", "Rejected", "Accepted"].map((s) => (
                      <Select.Option key={s} value={s}>
                        {s}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
              </div>
            </div>
            <div
              className="container mt-0"
              style={{ maxWidth: "100%", marginLeft: "20px", marginRight: "40px" }}
            >
              <div className="row d-flex align-items-center justify-content-start">
                <div className="col-12 col-md-2 mb-3 text-start">
                  <Button
                    disabled={loading}
                    className="btn btn-submit"
                    onClick={handleSearchApplications}
                    style={{ height: "38px", width: "55%" }}
                  >
                    {loading ? "Searching..." : "Search"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card table-list-card-grid">
          <div className="card-body">
            <div className="table-top"></div>
            <Tabs defaultActiveKey="1">
              <Tabs.TabPane tab="Pending" key="1">
                <div className="table-responsive">
                  <Table
                    loading={loading}
                    columns={applicationColumns}
                    dataSource={applications}
                    rowKey="_id"
                    pagination={{
                      current: pagination.current,
                      pageSize: pagination.pageSize,
                      pageSizeOptions: ["5", "10", "20", "50"],
                      total: pagination.total,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      showTotal: (total) => `Total ${total} items`,
                    }}
                    onChange={handleTableChange}
                  />
                </div>
              </Tabs.TabPane>
              <Tabs.TabPane tab="Scheduled" key="2">
                <div className="table-responsive">
                  <Table
                    loading={loading}
                    columns={interviewColumns}
                    dataSource={interviews}
                    rowKey="_id"
                    pagination={{
                      current: interviewPagination.current,
                      pageSize: interviewPagination.pageSize,
                      pageSizeOptions: ["5", "10", "20", "50"],
                      total: interviewPagination.total,
                      showSizeChanger: true,
                      showQuickJumper: true,
                      showTotal: (total) => `Total ${total} items`,
                    }}
                    onChange={handleInterviewTableChange}
                  />
                </div>
              </Tabs.TabPane>
              <div className="table-responsive" style={{ display: "none" }}>
                <Table
                  loading={loading}
                  columns={onBoardColumns}
                  dataSource={interviews}
                  rowKey="_id"
                  pagination={{
                    current: interviewPagination.current,
                    pageSize: interviewPagination.pageSize,
                    pageSizeOptions: ["5", "10", "20", "50"],
                    total: interviewPagination.total,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total) => `Total ${total} items`,
                  }}
                  onChange={handleInterviewTableChange}
                />
              </div>
            </Tabs>
          </div>
        </div>
      </div>

      <Modal
        zIndex={200000}
        title="Apply for Job"
        open={openApplyModal}
        onCancel={() => {
          setOpenApplyModal(false);
          form.resetFields();
        }}
        footer={null}
        maskClosable={false}
        closable={true}
      >
        <Form form={form} onFinish={onApplyFinish} layout="vertical">
          <Form.Item
            name="job_id"
            label="Job Posting"
            rules={[{ required: true, message: "Please select a job" }]}
          >
            <Select
              placeholder="Select a job"
              showSearch
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
            >
              {jobPostings.map((job) => (
                <Select.Option key={job._id} value={job._id} label={job.title}>
                  {renderJobOption(job)}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="candidate_name"
            label="Candidate Name"
            rules={[{ required: true, message: "Please enter your name" }]}
          >
            <Input placeholder="Enter your name" />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: "Please enter your email" },
              { type: "email", message: "Please enter a valid email" },
            ]}
          >
            <Input placeholder="Enter your email" />
          </Form.Item>
          <Form.Item
            name="resume"
            label="Resume"
            valuePropName="file"
            rules={[{ required: true, message: "Please upload your resume" }]}
          >
            <Upload
              accept=".pdf"
              maxCount={1}
              beforeUpload={() => false}
            >
              <Button>Upload Resume (PDF only)</Button>
            </Upload>
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              Submit Application
            </Button>
            <Button
              onClick={() => {
                setOpenApplyModal(false);
                form.resetFields();
              }}
              style={{ marginLeft: 8 }}
            >
              Cancel
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        zIndex={200000}
        title="Application Details"
        open={openDetailsModal}
        onCancel={() => setOpenDetailsModal(false)}
        footer={[
          <Button key="close" className="btn btn-cancel" onClick={() => setOpenDetailsModal(false)}>
            Close
          </Button>,
        ]}
      >
        {selectedApplication && (
          <div>
            <p><strong>Job Title:</strong> {selectedApplication.job_id?.title || "N/A"}</p>
            <p><strong>Candidate Name:</strong> {selectedApplication.candidate_name}</p>
            <p><strong>Email:</strong> {selectedApplication.email}</p>
            <p><strong>Status:</strong> {selectedApplication.status}</p>
            <p><strong>Applied Date:</strong> {selectedApplication.applied_at ? moment(selectedApplication.applied_at).format("YYYY-MM-DD") : "N/A"}</p>
            <p><strong>Resume:</strong> {selectedApplication.resume?.originalName || "N/A"}</p>
            {selectedApplication.resume?.contentType === "application/pdf" && (
              <Button
                className="btn btn-submit"
                onClick={() => handleDownloadResume(selectedApplication.resume)}
              >
                Download Resume
              </Button>
            )}
          </div>
        )}
      </Modal>

      <Modal
        zIndex={200000}
        title="Schedule Interview"
        open={openScheduleModal}
        onCancel={() => {
          setOpenScheduleModal(false);
          scheduleForm.resetFields();
        }}
        footer={null}
        maskClosable={false}
        closable={true}
      >
        <Form form={scheduleForm} onFinish={onScheduleInterviewFinish} layout="vertical">
          <Form.Item
            name="application_id"
            label="Application"
            initialValue={selectedApplication?.candidate_name}
          >
            <Input disabled value={selectedApplication?.candidate_name} />
          </Form.Item>
          <Form.Item
            name="email"
            label="Email"
            initialValue={selectedApplication?.email}
          >
            <Input disabled value={selectedApplication?.email} />
          </Form.Item>
  <Form.Item
            name="interviewers"
            label="Interviewers"
            rules={[{ required: true, message: "Please select at least one interviewer" }]}
          >
            <Select
              mode="multiple"
              placeholder="Select interviewers"
              allowClear
            >
              {employees.map((employee) => (
                <Select.Option key={employee._id} value={employee._id}>
                  {employee.full_name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="date"
            label="Interview Date"
            rules={[{ required: true, message: "Please select a date" }]}
          >
            <DatePicker showTime format="YYYY-MM-DD HH:mm" style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="mode"
            label="Interview Mode"
            rules={[{ required: true, message: "Please select an interview mode" }]}
          >
            <Select placeholder="Select mode">
              <Select.Option value="Physical">Physical</Select.Option>
              <Select.Option value="Online">Online</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="details"
            label="Details (Meeting Link or Location)"
            rules={[{ required: true, message: "Please enter meeting link or location" }]}
          >
            <Input.TextArea placeholder="Enter meeting link (for Online) or location (for Physical)" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              Schedule Interview
            </Button>
            <Button
              onClick={() => {
                setOpenScheduleModal(false);
                scheduleForm.resetFields();
              }}
              style={{ marginLeft: 8 }}
            >
              Cancel
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        zIndex={200000}
        title="Interview Details"
        open={openInterviewDetailsModal}
        onCancel={() => setOpenInterviewDetailsModal(false)}
        footer={[
          <Button key="close" className="btn btn-cancel" onClick={() => setOpenInterviewDetailsModal(false)}>
            Close
          </Button>,
        ]}
      >
        {selectedInterview && (
          <div>
            <p><strong>Job Title:</strong> {selectedInterview.application_id?.job_id?.title || "N/A"}</p>
            <p><strong>Candidate Name:</strong> {selectedInterview.application_id?.candidate_name || "N/A"}</p>
            <p><strong>Interviewers:</strong> {selectedInterview.interviewers?.map(emp => emp.full_name).join(', ') || "N/A"}</p>
            <p><strong>Date:</strong> {selectedInterview.date ? moment(selectedInterview.date).format("YYYY-MM-DD HH:mm") : "N/A"}</p>
            <p><strong>Mode:</strong> {selectedInterview.mode || "N/A"}</p>
            <p><strong>Details:</strong> {selectedInterview.details || "N/A"}</p>
            <p><strong>Status:</strong> {selectedInterview.status || "N/A"}</p>
            <p><strong>Feedback:</strong> {selectedInterview.feedback || "N/A"}</p>
            {selectedInterview.offerLetter && (
              <p>
                <strong>Offer Letter:</strong>{" "}
                <Button
                  className="btn btn-submit"
                  onClick={() => handleDownloadOfferLetter(selectedInterview.offerLetter._id)}
                >
                  Download
                </Button>
              </p>
            )}
          </div>
        )}
      </Modal>

      <Modal
        zIndex={200000}
        title="Update Interview"
        open={openUpdateInterviewModal}
        onCancel={() => {
          setOpenUpdateInterviewModal(false);
          updateForm.resetFields();
        }}
        footer={null}
        maskClosable={false}
        closable={true}
      >
        <Form form={updateForm} onFinish={onUpdateInterviewFinish} layout="vertical">
          <Form.Item
            name="feedback"
            label="Feedback"
          >
            <Input.TextArea placeholder="Enter feedback" />
          </Form.Item>
          <Form.Item
            name="status"
            label="Status"
            rules={[{ required: true, message: "Please select a status" }]}
          >
            <Select placeholder="Select status">
              {["Scheduled", "Completed", "Cancelled"].map((s) => (
                <Select.Option key={s} value={s}>
                  {s}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              Update Interview
            </Button>
            <Button
              onClick={() => {
                setOpenUpdateInterviewModal(false);
                updateForm.resetFields();
              }}
              style={{ marginLeft: 8 }}
            >
              Cancel
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        zIndex={200000}
        title="Generate Offer Letter"
        open={openOfferModal}
        onCancel={() => {
          setOpenOfferModal(false);
          offerForm.resetFields();
        }}
        footer={null}
        maskClosable={false}
        closable={true}
      >
        <Form form={offerForm} onFinish={onGenerateOfferLetterFinish} layout="vertical">
          <Form.Item
            name="candidate_name"
            label="Candidate Name"
            initialValue={selectedInterview?.application_id?.candidate_name}
          >
            <Input disabled />
          </Form.Item>
          <Form.Item
            name="job_title"
            label="Job Title"
            initialValue={selectedInterview?.application_id?.job_id?.title}
          >
            <Input disabled />
          </Form.Item>
          <Form.Item
            name="branch"
            label="Branch"
            rules={[{ required: true, message: "Please select a branch" }]}
          >
            <Select placeholder="Select branch">
              {branchOptions.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="onBoard_date"
            label="Onboarding Date"
            rules={[{ required: true, message: "Please select an onboarding date" }]}
          >
            <DatePicker format="YYYY-MM-DD" style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="offer_details"
            label="Offer Details"
            rules={[{ required: true, message: "Please enter offer details" }]}
          >
            <Input.TextArea placeholder="Enter compensation and benefits details" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading}>
              Generate Offer Letter
            </Button>
            {selectedInterview?.offerLetter?._id && selectedInterview.offerLetter.status !== 'Sent' ? (
              <Button
                type="default"
                onClick={() => handleSendOfferLetterEmail(selectedInterview.offerLetter._id)}
                loading={loading}
                style={{ marginLeft: 8 }}
              >
                Send Email
              </Button>
            ) : null}
            <Button
              onClick={() => {
                setOpenOfferModal(false);
                offerForm.resetFields();
              }}
              style={{ marginLeft: 8 }}
            >
              Cancel
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Change Page Size"
        open={pageSizeModalVisible}
        onOk={handlePageSizeChange}
        onCancel={() => setPageSizeModalVisible(false)}
        footer={[
          <Button
            key="cancel"
            className="btn btn-cancel"
            onClick={() => setPageSizeModalVisible(false)}
          >
            Cancel
          </Button>,
          <Button key="submit" className="btn btn-submit" onClick={handlePageSizeChange}>
            Apply
          </Button>,
        ]}
      >
        <div className="form-group">
          <label>Items per page:</label>
          <Select
            style={{ width: "100%" }}
            value={tempPageSize}
            onChange={(value) => setTempPageSize(value)}
          >
            {pageSizeOptions.map((size) => (
              <Select.Option key={size} value={size}>
                {size}
              </Select.Option>
            ))}
          </Select>
        </div>
      </Modal>
    </div>
  );
};

export default Interviews;